/**
 * Shared data contracts for levels, entities and gameplay objects.
 *
 * A level has two layers, mirroring the reference editor:
 *   - `tiles`   the terrain layer: a flat grid of ground/solid blocks
 *   - `objects` the object layer: everything the designer places on top,
 *               each occupying a tile rectangle
 *
 * Collision geometry for both layers is merged into a single solid grid when
 * the level loads (see `buildSolidGrid`), so the physics code only ever
 * queries one array.
 */

import type { SolidQuery } from '../engine/collision'
// Type-only, so this is erased at runtime and the module cycle is harmless.
import type { PlacedObject } from './level'

/** Terrain tile ids. `EMPTY` is air. */
export const Terrain = {
  EMPTY: 0,
  GROUND: 1,
  HARD: 2,
  BRICK: 3,
  /** Solid but visually a "hard" block used for ceilings/structures. */
  SLOPE_L: 4,
  SLOPE_R: 5,
  /** One-way platform: solid only when landing on it from above. */
  PLATFORM: 6,
} as const

/** Terrain ids that block movement from every direction. */
export const SOLID_TERRAIN: ReadonlySet<number> = new Set([
  Terrain.GROUND,
  Terrain.HARD,
  Terrain.BRICK,
  Terrain.SLOPE_L,
  Terrain.SLOPE_R,
])

/** Terrain ids that block movement only when falling onto them. */
export const ONEWAY_TERRAIN: ReadonlySet<number> = new Set([Terrain.PLATFORM])

/** Palette categories, matching the tabs of the Tavi's Workshop editor. */
export type PaletteCategory =
  | 'terrain'
  | 'blocks'
  | 'pipes'
  | 'hazards'
  | 'platforms'
  | 'enemies'
  | 'items'
  | 'scenery'

/** How an object's collision behaves. */
export const Solidity = {
  /** Not solid at all (decor, coins, enemies). */
  NONE: 0,
  /** Blocks from all sides. */
  FULL: 1,
  /** Blocks only a downward landing (one-way platform, bridge). */
  ONEWAY: 2,
  /** Solid only when struck from below (brick / crate block). */
  HEAD: 3,
} as const

/** Context handed to object behaviours each step. */
export interface BehaviourContext {
  /** Ticks elapsed since the level started. */
  tick: number
  /** Owning level, for terrain queries and spawning. */
  level: LevelRuntime
  /** The player, for interactions. */
  player: PlayerLike
  /** Free-form scratch object for the behaviour to stash state on. */
  state: Record<string, unknown>
}

/** Minimal player surface object behaviours rely on. */
export interface PlayerLike {
  x: number
  y: number
  w: number
  h: number
  vx: number
  vy: number
  facing: 1 | -1
  power: 'small' | 'big'
  alive: boolean
  /** Reset transient movement and power state at a spawn point. */
  placeAt?(x: number, y: number): void
  /** Give the player vertical velocity, e.g. for a stomp bounce. */
  setVy(v: number): void
  /** Grow the player (expansion-cell). */
  grow(): void
  /** Kill the player outright. */
  kill(): void
  /**
   * Take a hit: small dies, big shrinks. Optional so a minimal host can
   * implement only the verbs it needs, but the concrete `Player` provides it
   * and every damage source routes through it when present.
   */
  hurt?(): void
}

/** Level surface exposed to behaviours (implemented by `Level`). */
/**
 * Level surface exposed to behaviours (implemented by `Level`).
 *
 * It extends `SolidQuery` so a level can be passed straight to
 * `EntityBase.physicsStep` and the collision helpers with no adapter: entity AI
 * asks exactly the same "is this tile solid" question the player does.
 */
export interface LevelRuntime extends SolidQuery {
  width: number
  height: number
  /** Spawn a runtime entity (enemy, pickup, projectile). */
  spawn(
    kind: string,
    x: number,
    y: number,
    props?: Record<string, unknown>,
  ): void
  /** Remove a placed object by its placement index. */
  removeObject(index: number): void
  /** Award a coin. */
  addCoin(): void
  /** Award score. */
  addScore(n: number): void
  /** Move the respawn point. */
  setCheckpoint(tx: number, ty: number): void
  /** Trigger the level-complete sequence. */
  finish(): void
  /** The placed object occupying a tile, if any. */
  objectAt(tx: number, ty: number): PlacedObject | null
  /** Global on/off switch state, driving On/Off platforms. */
  onOffActive: boolean
}

/** Behaviour hooks an object type can implement. */
export interface ObjectBehaviour {
  /** Called every simulation step while active. */
  update?(ctx: BehaviourContext): void
  /** Player head-bumped this object from below. */
  onHeadBump?(ctx: BehaviourContext): void
  /** Player struck this object from the side. */
  onSideHit?(ctx: BehaviourContext): void
  /** Player stomped this object from above. */
  onStomp?(ctx: BehaviourContext): void
}

/** A designer-placed object: an id plus the tile rectangle it occupies. */
export interface ObjectPlacement {
  /** Key into the object registry (`src/game/objects.ts`). */
  id: string
  /** Tile coordinates of the object's bottom-left tile. */
  tx: number
  ty: number
  /** Optional per-instance configuration (e.g. moving platform distance). */
  props?: Record<string, number | string | boolean>
}

/** Visual themes, each with its own palette + background treatment. */
export type ThemeId = 'overworld' | 'underground' | 'night' | 'castle' | 'sky'

/** Serialised level, saved as JSON. */
export interface LevelData {
  name: string
  author?: string
  /** Course width in tiles. */
  width: number
  /** Course height in tiles. */
  height: number
  /** Visual theme id. */
  theme: ThemeId
  /** Seconds on the clock. */
  time: number
  /** Terrain layer, row-major, `width * height` entries. */
  tiles: number[]
  /** Object layer. */
  objects: ObjectPlacement[]
  /** Player start position in tile coords. */
  startX: number
  startY: number
}
